import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewBookingStatsItem } from '../../../../types/dashboard';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../../../shared/GenericTable';
import {
    getBookingStatusChipTone,
    getReportChipSx,
} from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

type AddOnsBookedBookingsListProps = {
    bookings: NewBookingStatsItem[];
};

const capitalize = (value?: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export default function AddOnsBookedBookingsList({
    bookings,
}: AddOnsBookedBookingsListProps) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const navigate = useNavigate();
    const theme = useTheme();

    const rows = bookings ?? [];
    const reservationIds = useMemo(() => rows.map((row) => row.id), [rows]);

    const columns: MRT_ColumnDef<NewBookingStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'moduleName',
                header: 'Experience',
                size: 200,
            },
            {
                accessorKey: 'ownerName',
                header: 'Host',
                size: isMobile || isIpad ? 80 : 150,
                Cell: ({ cell }) => capitalize(cell.getValue<string>()),
            },
            {
                accessorKey: 'moduleType',
                header: 'Type',
                size: 80,
                Cell: ({ cell }) => capitalize(cell.getValue<string>()),
            },
            {
                accessorKey: 'id',
                header: 'Reservation Ref',
                size: isMobile || isIpad ? 80 : 110,
                Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            },
            {
                accessorKey: 'bookerCompany',
                header: 'Booked By',
                size: isMobile || isIpad ? 80 : 150,
                Cell: ({ cell }) => capitalize(cell.getValue<string>()),
            },
            {
                accessorKey: 'bookedFor',
                header: 'Guest',
                size: isMobile || isIpad ? 80 : 150,
                Cell: ({ cell }) => capitalize(cell.getValue<string>()),
            },
            {
                accessorKey: 'createdAtDate',
                header: 'Created',
                size: isMobile ? 120 : isIpad ? 100 : 110,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                accessorKey: 'startDate',
                header: 'Reservation From',
                size: isMobile ? 120 : isIpad ? 100 : 110,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                accessorKey: 'endDate',
                header: 'Reservation To',
                size: isMobile ? 120 : isIpad ? 100 : 110,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 150,
                Cell: ({ cell }) => (
                    <CustomChip
                        label={capitalize(cell.getValue<string>())}
                        sx={getReportChipSx(
                            getBookingStatusChipTone(cell.getValue<string>()),
                            theme,
                        )}
                        variant='outlined'
                    />
                ),
            },
        ],
        [isIpad, isMobile, theme],
    );

    const handleRowClick = (row: NewBookingStatsItem) => {
        navigate(
            `/reservations/${row.moduleName.replace(/ /g, '').toLowerCase()}/${row.id}`,
            {
                state: {
                    name: 'viewReservation',
                    component: 'dashboard',
                    url: 'dashboard/add-ons-booked',
                    params: row,
                    index: reservationIds.findIndex((id) => id === row.id),
                    reservations: reservationIds,
                },
            },
        );
    };

    if (!rows.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual add-on bookings found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={rows}
                tableName='Add-ons Booked Report'
                paginationItemLabel='bookings'
                cursor='pointer'
                onRowClick={handleRowClick}
            />
        </Box>
    );
}
